import type { PlanChangeRequest, PlanChangeStatus } from '@/types/billing';
import { CUT_FEE_MAP } from '@consts/company';
import { AppRoutes } from '@consts/routes';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Chip, Link, Paper, Typography } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useState } from 'react';

dayjs.extend(utc);

enum LoadingStateOpts {
    Approving = 1,
    Rejecting = 2,
    Canceling = 3,
}

export const RequestCard = ({
    handleAction,
    request,
    actions,
}: {
    handleAction: (planId: number, status: Extract<PlanChangeStatus, 'Approved' | 'Rejected'>) => Promise<void>;
    request: PlanChangeRequest;
    actions?: boolean;
}) => {
    const [loadingState, setLoadingState] = useState<Record<LoadingStateOpts, boolean>>({
        [LoadingStateOpts.Approving]: false,
        [LoadingStateOpts.Rejecting]: false,
        [LoadingStateOpts.Canceling]: false,
    });

    const updateLoading = (key: LoadingStateOpts, newValue: boolean) => {
        setLoadingState((prev) => {
            return {
                ...prev,
                [key]: newValue,
            };
        });
    };

    const handelActionWrapper = async (
        planId: number,
        status: Extract<PlanChangeStatus, 'Approved' | 'Rejected'>,
        key: LoadingStateOpts
    ) => {
        updateLoading(key, true);
        await handleAction(planId, status);
        updateLoading(key, false);
    };

    return (
        <Paper
            key={`${request.StationID}-${request.TargetPlanID}-${request.EffectiveDate}`}
            sx={{ p: 1.5, mb: 1, border: '1px solid #e0e0e0', borderRadius: 2 }}
            elevation={0}>
            <Box display='flex' justifyContent='space-between' alignItems='center' pb={1}>
                <Box display='flex' alignItems='center' gap={1}>
                    <Typography variant='subtitle2' fontWeight={600}>
                        {request.SerialNumber ? `Serial # ${request.SerialNumber}` : `Station #${request.StationID}`}
                    </Typography>
                    <Chip
                        label={`${CUT_FEE_MAP[request.CurrentPlanID]} → ${CUT_FEE_MAP[request.TargetPlanID]}`}
                        size='small'
                        sx={{
                            backgroundColor: 'rgba(229, 57, 53, 0.2)',
                            color: 'rgba(229, 57, 53, 1)',
                            fontSize: '11px',
                            fontWeight: 600,
                            height: 20,
                        }}
                    />
                </Box>

                {actions && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <LoadingButton
                            loading={loadingState[LoadingStateOpts.Approving]}
                            variant='contained'
                            color='success'
                            size='small'
                            onClick={() => handelActionWrapper(request.PlanID, 'Approved', LoadingStateOpts.Approving)}>
                            Approve
                        </LoadingButton>
                        <LoadingButton
                            loading={loadingState[LoadingStateOpts.Rejecting]}
                            variant='contained'
                            color='error'
                            size='small'
                            onClick={() => handelActionWrapper(request.PlanID, 'Rejected', LoadingStateOpts.Rejecting)}>
                            Reject
                        </LoadingButton>
                    </Box>
                )}
            </Box>

            <Typography variant='body2' color='text.primary'>
                Company: {`${request.CompanyName}`}
            </Typography>
            <Typography variant='body2' color='text.primary' sx={{ mt: 0.5 }}>
                Location: {`${request.LocationName}`}
            </Typography>
            <Typography variant='body2' color='text.primary' sx={{ mt: 0.5 }}>
                {`Effective: ${dayjs.utc(request.EffectiveDate).format('MMM DD, YYYY')}`}
            </Typography>
            {request.Justification && (
                <Typography variant='body2' color='text.primary' sx={{ mt: 0.5 }}>
                    {`Justification: ${request.Justification}`}
                </Typography>
            )}
            <Typography
                variant='caption'
                color='text.primary'>{`Scheduled by ${request.CreatedByUserEmail}`}</Typography>

            <Box display={'flex'} gap={1} flexDirection={'row'} justifyContent={'space-between'} flex={1}>
                {/* Link to manage system */}
                <Box mt={0.5}>
                    <Link
                        component={RouterLink}
                        to={`${AppRoutes.Organizations}?type=system&id=${request.StationID}`}
                        underline='hover'
                        fontSize='0.8rem'>
                        View System Details
                    </Link>
                </Box>

                {/* Action button, ability to cancel the scheduled change */}
                {!actions && (
                    <Box mt={0.5}>
                        <LoadingButton
                            loading={Boolean(loadingState[LoadingStateOpts.Canceling])}
                            variant='contained'
                            color='error'
                            size='small'
                            onClick={() => handelActionWrapper(request.PlanID, 'Rejected', LoadingStateOpts.Canceling)}>
                            Cancel
                        </LoadingButton>
                    </Box>
                )}
            </Box>
        </Paper>
    );
};
